import {
  CurrencyOption as CurrencyOptionBase,
  OptionItemInfoText,
  OptionItemInfoTooltip,
} from '@frontend/ui-library';
import { Currency } from '../CurrencySelection';
import { getIsUSDC, getIsUSDT, isCurrencyAvailable } from '../utils';
import { createAssetSrc } from '@frontend/utils';

interface CurrencyOptionProps {
  currency: Currency;
  disabled: boolean;
  onSelect: (symbol: string) => void;
}

export const CurrencyOption = ({
  currency,
  disabled,
  onSelect,
}: CurrencyOptionProps) => {
  const isAvailable = isCurrencyAvailable(currency.blockchains);
  const isUSDT = getIsUSDT(currency.symbol);
  const isUSDC = getIsUSDC(currency.symbol);

  const shouldShowReason = !isAvailable && currency.blockchains.length === 1;
  const shouldShowUSDTUnsupportedMessage = !isAvailable && isUSDT;
  const shouldShowUSDCUnavailableMessage = !isAvailable && isUSDC;

  const getDisabledMessage = () => {
    if (shouldShowUSDTUnsupportedMessage) return 'Not supported';

    if (!shouldShowReason) return 'Temporarily unavailable';

    return currency.blockchains[0].reason;
  };

  const getTooltipContent = () => {
    if (shouldShowUSDTUnsupportedMessage) {
      return 'USDT is no longer supported due to MICA regulatory constraints';
    }

    if (shouldShowUSDCUnavailableMessage) {
      return 'USDC is temporarily unavailable due to regulatory constraints. We’re working to overcome this limitation';
    }

    if (!shouldShowReason) {
      return 'This can happen due to payment expiration time, amount to pay or temporarily unavailable networks';
    }

    return null;
  };

  const renderTooltip = () => {
    const content = getTooltipContent();
    return content ? <OptionItemInfoTooltip tooltipContent={content} /> : null;
  };

  return (
    <CurrencyOptionBase
      showInfoTooltipOnHover={true}
      supportedNetworks={currency.blockchains.map((c) => ({
        iconSrc: createAssetSrc(c.chainSymbol, 'networks'),
        alt: c.chainSymbol,
      }))}
      currencyIconSrc={createAssetSrc(currency.symbol, 'tickers')}
      currencyName={currency.name}
      currencySymbol={currency.symbol}
      onClick={() => onSelect(currency.symbol)}
      disabled={disabled || !isAvailable}
      customRightTopSlot={isAvailable ? null : ''}
      customRightBottomSlot={
        isAvailable ? null : (
          <OptionItemInfoText
            text={getDisabledMessage()}
            trailingSlot={renderTooltip()}
          />
        )
      }
    />
  );
};
